/**
 * Stable machine-readable error codes returned in `HttpsError.details.code`.
 *
 * Clients switch on these; they never parse messages. Every code maps to a
 * short, non-technical user-facing string (see `USER_FACING_ERROR_MESSAGE`) so
 * stack traces, Firebase internals and Agora credentials never reach a screen.
 */
export const APP_ERROR = {
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  INVALID_ARGUMENT: 'INVALID_ARGUMENT',
  APP_CHECK_REQUIRED: 'APP_CHECK_REQUIRED',
  RATE_LIMITED: 'RATE_LIMITED',

  INVALID_HOTEL: 'INVALID_HOTEL',
  INVALID_ROOM: 'INVALID_ROOM',
  HOTEL_INACTIVE: 'HOTEL_INACTIVE',
  ROOM_UNAVAILABLE: 'ROOM_UNAVAILABLE',
  GUEST_SESSION_REQUIRED: 'GUEST_SESSION_REQUIRED',
  GUEST_PHONE_REQUIRED: 'GUEST_PHONE_REQUIRED',
  GUEST_NOT_AUTHENTICATED: 'GUEST_NOT_AUTHENTICATED',

  HOTEL_SETUP_REQUIRED: 'HOTEL_SETUP_REQUIRED',
  HOTEL_ALREADY_EXISTS: 'HOTEL_ALREADY_EXISTS',
  DUPLICATE_ROOM_NUMBER: 'DUPLICATE_ROOM_NUMBER',
  ROOM_NOT_DELETABLE: 'ROOM_NOT_DELETABLE',
  ROOM_STATUS_LOCKED: 'ROOM_STATUS_LOCKED',
  UNKNOWN_ROOM_TYPE: 'UNKNOWN_ROOM_TYPE',
  DUPLICATE_ROOM_TYPE: 'DUPLICATE_ROOM_TYPE',
  STAFF_NOT_FOUND: 'STAFF_NOT_FOUND',
  STAFF_ALREADY_EXISTS: 'STAFF_ALREADY_EXISTS',
  PHONE_IN_USE: 'PHONE_IN_USE',
  ROOM_NOT_READY: 'ROOM_NOT_READY',
  ROOM_NOT_OCCUPIED: 'ROOM_NOT_OCCUPIED',
  BOOKING_NOT_FOUND: 'BOOKING_NOT_FOUND',
  GUEST_COUNT_MISMATCH: 'GUEST_COUNT_MISMATCH',
  SERVICE_NOT_FOUND: 'SERVICE_NOT_FOUND',
  DUPLICATE_SERVICE_NAME: 'DUPLICATE_SERVICE_NAME',
  MENU_ITEM_NOT_FOUND: 'MENU_ITEM_NOT_FOUND',
  UNKNOWN_MENU_CATEGORY: 'UNKNOWN_MENU_CATEGORY',
  DUPLICATE_MENU_CATEGORY: 'DUPLICATE_MENU_CATEGORY',
  MENU_CATEGORY_IN_USE: 'MENU_CATEGORY_IN_USE',
  SERVICE_DISABLED: 'SERVICE_DISABLED',
  EMPTY_REQUEST: 'EMPTY_REQUEST',
  REQUEST_NOT_FOUND: 'REQUEST_NOT_FOUND',
  INVALID_REQUEST_TRANSITION: 'INVALID_REQUEST_TRANSITION',
  DUPLICATE_WIFI_SSID: 'DUPLICATE_WIFI_SSID',
  OFFER_NOT_FOUND: 'OFFER_NOT_FOUND',
  DUPLICATE_OFFER_NAME: 'DUPLICATE_OFFER_NAME',
  PLACE_NOT_FOUND: 'PLACE_NOT_FOUND',
  DUPLICATE_PLACE_NAME: 'DUPLICATE_PLACE_NAME',
  TOUR_PACKAGE_NOT_FOUND: 'TOUR_PACKAGE_NOT_FOUND',
  DUPLICATE_TOUR_PACKAGE_NAME: 'DUPLICATE_TOUR_PACKAGE_NAME',

  NO_RECEPTIONIST_AVAILABLE: 'NO_RECEPTIONIST_AVAILABLE',
  RECEPTIONIST_NOT_FOUND: 'RECEPTIONIST_NOT_FOUND',
  RECEPTIONIST_INACTIVE: 'RECEPTIONIST_INACTIVE',
  NOT_ASSIGNED_TO_CALL: 'NOT_ASSIGNED_TO_CALL',

  CALL_NOT_FOUND: 'CALL_NOT_FOUND',
  CALL_ALREADY_ACCEPTED: 'CALL_ALREADY_ACCEPTED',
  CALL_ALREADY_ENDED: 'CALL_ALREADY_ENDED',
  CALL_IN_PROGRESS: 'CALL_IN_PROGRESS',
  INVALID_CALL_TRANSITION: 'INVALID_CALL_TRANSITION',

  AGORA_CONFIG_MISSING: 'AGORA_CONFIG_MISSING',
  AGORA_TOKEN_FAILED: 'AGORA_TOKEN_FAILED',

  INTERNAL: 'INTERNAL',
} as const;

export type AppErrorCode = (typeof APP_ERROR)[keyof typeof APP_ERROR];

/**
 * Copy shown to end users. Intentionally vague about internals: a guest never
 * needs to know whether the hotel id was wrong or the room was out of service.
 */
export const USER_FACING_ERROR_MESSAGE: Readonly<Record<AppErrorCode, string>> =
  {
    UNAUTHENTICATED: 'Please reload the page and try again.',
    PERMISSION_DENIED: "You don't have access to this.",
    INVALID_ARGUMENT: 'Something looks wrong with this request. Please try again.',
    APP_CHECK_REQUIRED: 'Please reload the page and try again.',
    RATE_LIMITED: 'Too many attempts. Please wait a moment and try again.',

    INVALID_HOTEL: 'This link is not valid. Please scan the code in your room.',
    INVALID_ROOM: 'This link is not valid. Please scan the code in your room.',
    HOTEL_INACTIVE: 'Reception is not available right now.',
    ROOM_UNAVAILABLE: 'This room is not available for calls right now.',
    GUEST_SESSION_REQUIRED: 'Please reload the page and try again.',
    GUEST_PHONE_REQUIRED: 'Please enter the mobile number given at check-in.',
    GUEST_NOT_AUTHENTICATED:
      'This mobile number does not match the booking for this room. Please check it and try again.',

    HOTEL_SETUP_REQUIRED: 'Finish setting up your hotel first.',
    HOTEL_ALREADY_EXISTS: 'This account already has a hotel.',
    DUPLICATE_ROOM_NUMBER: 'A room with this number already exists.',
    ROOM_NOT_DELETABLE:
      'Only rooms that are Ready to Occupy or Out of Service can be deleted.',
    ROOM_STATUS_LOCKED:
      'This room follows check-in, check-out and housekeeping — its status cannot be set by hand.',
    UNKNOWN_ROOM_TYPE: 'Pick a room type from the list, or add a new one.',
    DUPLICATE_ROOM_TYPE: 'That room type already exists.',
    STAFF_NOT_FOUND: 'This staff member no longer exists.',
    STAFF_ALREADY_EXISTS: 'This person is already on your staff.',
    PHONE_IN_USE: 'This phone number is already used by another account.',
    ROOM_NOT_READY: 'This room is no longer ready to occupy.',
    ROOM_NOT_OCCUPIED: 'This room is not currently occupied.',
    BOOKING_NOT_FOUND: 'No active booking was found for this room.',
    GUEST_COUNT_MISMATCH:
      'The guests you added do not match the adult and child counts.',
    SERVICE_NOT_FOUND: 'This service no longer exists.',
    DUPLICATE_SERVICE_NAME: 'A service with this name already exists.',
    MENU_ITEM_NOT_FOUND: 'This item no longer exists.',
    UNKNOWN_MENU_CATEGORY: 'Pick a category from the list, or add a new one.',
    DUPLICATE_MENU_CATEGORY: 'That category already exists.',
    MENU_CATEGORY_IN_USE: 'Move or delete the items in this category first.',
    SERVICE_DISABLED: 'This service is not available right now.',
    EMPTY_REQUEST: 'Please choose at least one item, or tell us what you need.',
    REQUEST_NOT_FOUND: 'This request no longer exists.',
    INVALID_REQUEST_TRANSITION: 'This request can no longer be changed.',
    DUPLICATE_WIFI_SSID: 'A Wi-Fi network with this name already exists.',
    OFFER_NOT_FOUND: 'This offer no longer exists.',
    DUPLICATE_OFFER_NAME: 'An offer with this name already exists.',
    PLACE_NOT_FOUND: 'This place no longer exists.',
    DUPLICATE_PLACE_NAME: 'A place with this name already exists.',
    TOUR_PACKAGE_NOT_FOUND: 'This tour package no longer exists.',
    DUPLICATE_TOUR_PACKAGE_NAME: 'A tour package with this name already exists.',

    NO_RECEPTIONIST_AVAILABLE:
      "No one is at reception right now. Please try again shortly, or dial 0 from your room phone.",
    RECEPTIONIST_NOT_FOUND: 'Your account is not set up for calls.',
    RECEPTIONIST_INACTIVE: 'Your account is not active. Please contact your manager.',
    NOT_ASSIGNED_TO_CALL: 'This call is no longer available.',

    CALL_NOT_FOUND: 'This call has ended.',
    CALL_ALREADY_ACCEPTED: 'Another colleague answered this call.',
    CALL_ALREADY_ENDED: 'This call has ended.',
    CALL_IN_PROGRESS: 'A call is already in progress.',
    INVALID_CALL_TRANSITION: 'This call has ended.',

    AGORA_CONFIG_MISSING: "We couldn't connect to reception. Please try again.",
    AGORA_TOKEN_FAILED: "We couldn't connect to reception. Please try again.",

    INTERNAL: "We couldn't connect to reception. Please try again.",
  };

/** Client-side failures that never reach the server but need the same copy. */
export const CLIENT_ERROR = {
  MIC_PERMISSION_DENIED: 'MIC_PERMISSION_DENIED',
  MIC_NOT_FOUND: 'MIC_NOT_FOUND',
  AGORA_JOIN_FAILED: 'AGORA_JOIN_FAILED',
  NETWORK_UNAVAILABLE: 'NETWORK_UNAVAILABLE',
  AUTH_FAILED: 'AUTH_FAILED',
} as const;

export type ClientErrorCode = (typeof CLIENT_ERROR)[keyof typeof CLIENT_ERROR];

export const USER_FACING_CLIENT_MESSAGE: Readonly<
  Record<ClientErrorCode, string>
> = {
  MIC_PERMISSION_DENIED:
    'Microphone access is blocked. Allow the microphone in your browser settings to call reception.',
  MIC_NOT_FOUND: "We couldn't find a microphone on this device.",
  AGORA_JOIN_FAILED: "We couldn't connect to reception. Please try again.",
  NETWORK_UNAVAILABLE: 'You appear to be offline. Check your connection and try again.',
  AUTH_FAILED: 'Please reload the page and try again.',
};

export function userFacingMessage(code: string | undefined | null): string {
  if (code && code in USER_FACING_ERROR_MESSAGE) {
    return USER_FACING_ERROR_MESSAGE[code as AppErrorCode];
  }
  if (code && code in USER_FACING_CLIENT_MESSAGE) {
    return USER_FACING_CLIENT_MESSAGE[code as ClientErrorCode];
  }
  return USER_FACING_ERROR_MESSAGE.INTERNAL;
}
